/**
 * Choosing which stored version of an outreach message the founder is looking at.
 *
 * A field can have several: the control (base prompt), one per A/B arm, and the
 * founder's own edit. Which one is SHOWN is a display decision; which one is
 * SENT is decided by the backend at push time. The two must agree on the default
 * or the dialog would misrepresent what goes out.
 */

export interface OutreachMessageVariantView {
  id: string
  /** "control" | "arm-1" | "arm-2" | … | "custom". */
  arm: string
  text: string
  promptVersion: string | null
}

export const CONTROL_ARM = 'control'
export const CUSTOM_ARM = 'custom'

/**
 * The version a founder sees first.
 *
 * Their own edit wins, because a custom variant is the ONLY thing sent for that
 * field — opening on an arm would show copy that cannot go out. With no custom,
 * the control is what a lead is most likely to receive.
 */
export function defaultArm(variants: OutreachMessageVariantView[]): string {
  if (variants.some((v) => v.arm === CUSTOM_ARM)) return CUSTOM_ARM
  return CONTROL_ARM
}

/** Resolve the shown arm, falling back when a selection no longer exists. */
export function resolveShownArm(
  variants: OutreachMessageVariantView[],
  selected: string | undefined
): string {
  if (selected && variants.some((v) => v.arm === selected)) return selected
  return defaultArm(variants)
}

/**
 * Whether to draw the version switcher at all.
 *
 * One version means there is nothing to choose between — the state of every
 * startup that predates the A/B, and of any startup with the arms flag off.
 * Their dialog must look exactly as it always has.
 */
export function shouldShowVariantToggle(variants: OutreachMessageVariantView[]): boolean {
  return variants.length > 1
}

/**
 * What the version switcher is FOR, in the founder's terms.
 *
 * They are about to send copy under their own name and have just noticed it
 * comes in several flavours. Two things they need and nothing else: no single
 * investor sees more than one version, and the choice resolves on evidence
 * rather than being left to them. Our vocabulary — arm, control, experiment —
 * only teaches them they are being tested on, so none of it appears here.
 */
export const VARIANT_TOGGLE_EXPLAINER =
  'We wrote a few versions of this message. Each investor receives only one of ' +
  'them, so nobody gets a repeat. As replies come in we keep whichever version ' +
  'is working best and stop sending the rest. Edit any of them and yours is ' +
  'sent instead — for that message only.'

/**
 * Display order: control, then arms by name, then the founder's edit LAST.
 *
 * The API returns rows in whatever order the database yields. Left unsorted the
 * switcher renders "Custom A B C", which reads as though the founder's edit were
 * the first of the alternatives rather than the one that overrides them all.
 * Generated versions first, in the order they are labelled (A, B, C), with the
 * edit at the end where its ✕ sits next to nothing else.
 */
export function orderVariantsForDisplay<T extends { arm: string }>(variants: T[]): T[] {
  const rank = (arm: string) => (arm === CONTROL_ARM ? 0 : arm === CUSTOM_ARM ? 2 : 1)
  return [...variants].sort((a, b) => {
    const byRank = rank(a.arm) - rank(b.arm)
    return byRank !== 0 ? byRank : a.arm.localeCompare(b.arm)
  })
}

/**
 * Whether the generated versions are selectable.
 *
 * A custom variant is the ONLY thing sent for its field, so while one exists the
 * arms are shown but not choosable — offering the choice would imply the push
 * has one. Discarding the edit (the X on the Custom chip) restores it.
 */
export function generatedVersionsLocked(variants: OutreachMessageVariantView[]): boolean {
  return variants.some((v) => v.arm === CUSTOM_ARM)
}

/**
 * Founder-facing label for a version.
 *
 * Deliberately not our lab vocabulary: a founder reading "arm-1" on copy sent
 * under their own name learns only that they are being experimented on.
 */
export function variantLabel(arm: string): string {
  if (arm === CUSTOM_ARM) return 'Custom'
  if (arm === CONTROL_ARM) return 'A'
  const n = Number.parseInt(arm.replace(/\D/g, ''), 10)
  return Number.isFinite(n) ? String.fromCharCode(66 + Math.max(n - 1, 0)) : arm
}
